import { IndentValues } from "@/lib/utils/IndentValues";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import { Box, Indent, Space } from "@stenajs-webui/core";
import {
  Banner,
  SecondaryButton,
  ValueTable,
  ValueTableItem,
} from "@stenajs-webui/elements";
import { LoadingPanel } from "@stenajs-webui/panels";
import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useSailingsByRouteCodeDepartureDate } from "../hooks/UseSailingsByRouteCodeDepartureDate";
import { renderError } from "../lib/renderError";
import { parseTimeString } from "../lib/result-panel-transformers";
import styles from "./PriceCalculatorBookingForm.module.css";
import { transformBookingResultToEditableState } from "../lib/transformBookingResultToEditableState";
import { useMultilegSailingsByRouteCodeDepartureDate } from "../hooks/UseMultilegSailingsByRouteCodeDepartureDate";
import {
  PriceCalculatorContext,
  PriceCalculatorContextType,
} from "../PriceCalculatorContext";

interface Props {
  loading: boolean;
  localError: Error | undefined;
  clearLocalError: () => void;
}
export const PriceCalculatorBookingForm: React.FC<Props> = ({
  loading,
  localError,
  clearLocalError,
}) => {
  const { setState, defaultFormState, bookingResult } = useContext(
    PriceCalculatorContext
  ) as PriceCalculatorContextType;

  const firstLeg = 1;
  const bookingData = bookingResult?.bookingData[0];
  const bookingListData = bookingResult?.bookingData;
  const bookingDepartureDate = bookingListData?.find(
    (booking) => booking.bookingLegNumber === firstLeg
  )?.sailingBookingConsignment.departureDate;
  const bookingDepartureTime = bookingListData?.find(
    (booking) => booking.bookingLegNumber === firstLeg
  )?.sailingBookingConsignment.departureTime;

  const routeCodePairs = useMemo(() => {
    if (bookingListData) {
      return bookingListData.map((booking) => ({
        routeCode: booking?.routeCode?.code ?? "",
        departureDate: booking.sailingBookingConsignment.departureDate ?? "",
      }));
    }
    return [];
  }, [bookingListData]);

  const { sailings, fetchSailings, sailingDeparturesLoading } =
    useSailingsByRouteCodeDepartureDate();
  const { loading: multilegLoading, multilegSailings: combinedSailings } =
    useMultilegSailingsByRouteCodeDepartureDate(
      routeCodePairs,
      !(bookingData?.movementType.code === "M")
    );

  useEffect(() => {
    if (
      bookingData &&
      bookingDepartureDate &&
      (bookingData.movementType.code === "S" ||
        bookingData.movementType.code === "M")
    ) {
      fetchSailings({
        variables: {
          routeCode: bookingData.routeCode?.code ?? "",
          departureDate: bookingDepartureDate,
        },
      });
    }
  }, [bookingData, bookingDepartureDate, fetchSailings]);

  if (localError) {
    return (
      <>
        <Space num={SpaceValues.TWENTYFOUR} />
        {renderError(localError, clearLocalError)}
      </>
    );
  }

  if (loading || sailingDeparturesLoading || multilegLoading) {
    return (
      <>
        <Space num={SpaceValues.TWENTYFOUR} />
        <LoadingPanel />
      </>
    );
  }

  if (!bookingData || !bookingListData) {
    return (
      <>
        <Space num={SpaceValues.TWENTYFOUR} />
        <Banner
          variant={"info"}
          headerText={"Price not calculated yet"}
          text={
            "Please enter a booking number and select a version to calculate a price for it"
          }
        />
      </>
    );
  }

  const isStatusBooking = bookingData.priceCalculatorStatus === "BOOKING";
  const goods = isStatusBooking ? bookingData.bookingGoods : bookingData.goods;
  const ancillaries = (
    isStatusBooking ? bookingData.bookingArticles : bookingData.articles
  ).filter((ca) => ca.articleType.type === "ANC");

  const ancillariesOrGoodsExist = () =>
    goods.length > 0 || ancillaries.length > 0;

  const editBooking = () => {
    setState(
      transformBookingResultToEditableState({
        bookingData,
        bookingListData,
        bookingDepartureDate,
        bookingDepartureTime,
        combinedSailings: combinedSailings ?? [],
        defaultFormState,
        sailings,
      })
    );
  };

  return (
    <>
      <Space num={2} />
      <Box width={"10%"} alignSelf={"flex-end"}>
        <SecondaryButton label="Edit" onClick={() => editBooking()} />
      </Box>
      <Indent num={IndentValues.TWENTYFOUR} className={styles.bookingForm}>
        <ValueTable header={"Booking details"}>
          <ValueTableItem
            label={"Booking number"}
            value={
              bookingListData.length > 1
                ? bookingData.multiLegBookingNumber
                : bookingData?.bookingNumber
            }
          />
          <ValueTableItem
            label={"Booking status"}
            value={bookingData?.priceCalculatorStatus}
          />
          <ValueTableItem
            label={"Invoice number"}
            value={
              bookingData.bookingLegInvoice
                ? bookingData.bookingLegInvoice?.invoiceNo
                : "-"
            }
          />
          <ValueTableItem
            label={"Customer reference"}
            value={bookingData?.customerReference}
          />
          <ValueTableItem
            label={"Movement type"}
            value={bookingData?.movementType.description}
          />
        </ValueTable>

        <Space />
        <ValueTable header={"Sailing"}>
          <ValueTableItem
            label={"Route"}
            value={
              bookingListData.length > 1
                ? bookingData.multiLegRoute?.name
                : bookingData?.sailingBookingConsignment.route.name
            }
          />
          <ValueTableItem label={"Date"} value={bookingDepartureDate} />
          <ValueTableItem
            label={"Departure time"}
            value={parseTimeString(bookingDepartureTime ?? "")}
          />
        </ValueTable>

        <Space />
        <ValueTable header={"Vehicle"}>
          <ValueTableItem
            label={"Vehicle Type"}
            value={bookingData?.vehicleType.shortDescription}
          />
          <ValueTableItem
            label={"Vehicle reg"}
            value={bookingData?.vehicleRegistrationNumber}
          />
          <ValueTableItem
            label={"Trailer reg"}
            value={bookingData?.trailerRegistrationNumber}
          />
          <ValueTableItem
            label={"Vehicle length"}
            value={bookingData?.length}
          />
          <ValueTableItem label={"Vehicle width"} value={bookingData?.width} />
          <ValueTableItem
            label={"Vehicle weight"}
            value={bookingData?.weight}
          />
          <ValueTableItem
            label={"Loading preference"}
            value={bookingData.loadingPreferenceCode}
          />
          <ValueTableItem label="Plugins" value={bookingData.numberOfPlugins} />
        </ValueTable>

        <Space />
        <ValueTable header={"Drivers and passengers"}>
          <ValueTableItem
            label={"Adults"}
            value={bookingData?.numberOfAdults}
          />
          <ValueTableItem
            label={"Children"}
            value={bookingData?.numberOfChildren}
          />
          <ValueTableItem
            label={"Infants"}
            value={bookingData?.numberOfInfants}
          />
          <ValueTableItem
            label={"Number of sleepers"}
            value={bookingData?.numberOfSleepers}
          />
        </ValueTable>

        <Space />
        <ValueTable header={"Cargo Details"}>
          <ValueTableItem
            label={"Hazardous goods"}
            value={bookingData?.numberOfHazardousGoodsRows > 0 ? "YES" : "NO"}
          />
          <ValueTableItem
            label={"HG weight"}
            value={bookingData.hazardousGoodsTotalWeight}
          />
          <ValueTableItem
            label={"Number of HG rows"}
            value={bookingData?.numberOfHazardousGoodsRows}
          />
          <ValueTableItem
            label={"Qualified goods"}
            value={bookingData?.qualifiedGoods ? "YES" : "NO"}
          />
          <ValueTableItem
            label={"Animals"}
            value={bookingData?.hasLivestock ? "YES" : "NO"}
          />
          <ValueTableItem
            label={"Animal"}
            value={bookingData?.livestockType?.description ?? ""}
          />
          <ValueTableItem
            label={"Customs classification code"}
            value={bookingData?.customsClassificationCode ?? ""}
          />
        </ValueTable>

        <Space />
        <ValueTable header={"Conditions"}>
          <ValueTableItem
            label={"Standby"}
            value={bookingData?.standby ? "YES" : "NO"}
          />
          <ValueTableItem
            label={"Transit"}
            value={bookingData?.transit ? "YES" : "NO"}
          />
          <ValueTableItem
            label={"Sender country"}
            value={bookingData.senderCountryCode ?? "-"}
          />
          <ValueTableItem
            label={"Receiver country"}
            value={bookingData.receiverCountryCode ?? "-"}
          />
        </ValueTable>
        <Space />
        {ancillariesOrGoodsExist() && (
          <ValueTable header={"Goods & Ancillaries"}>
            {goods.map((gd) => (
              <ValueTableItem
                key={gd.id}
                label={`${gd.description} ${gd.goodsCode}`}
                value={gd.weight}
              />
            ))}
            {ancillaries.map((anc) => (
              <ValueTableItem
                key={anc.id}
                label={`${anc.articleType.name} ${anc.articleCode}`}
                value={anc.quantity}
              />
            ))}
          </ValueTable>
        )}
      </Indent>
    </>
  );
};
